/**
 * Create and edit. Same form, two entry points. Both pick a folder; on edit
 * that is a move, which the sync daemon carries out on disk.
 *
 * Both mount the rich editor over the body field when the device can run it.
 * Everything about saving is unchanged by that: Monaco writes through to the
 * textarea, so the submit path never learns it stopped being typed in.
 */

import { createNote, deleteNote, fetchFolders, fetchNote, updateNote } from "../api/notes";
import type { Note, NoteInput } from "../api/types";
import { noteForm } from "../components/noteForm";
import type { NoteFormHandle } from "../components/noteForm";
import { pageHeader } from "../components/pageHeader";
import { runAsyncView } from "../lib/asyncView";
import { el } from "../lib/dom";
import { placeholderTitle } from "../lib/placeholderTitle";
import { attachRichEditor, wantsRichEditor } from "../lib/richEditor";
import type { RichEditorHandle } from "../lib/richEditor";
import { homePath, notePath } from "../router/paths";
import { navigate } from "../router/router";
import type { PageContext, PageView } from "../router/router";

const DEFAULT_FOLDER = "inbox";

/**
 * Holds the editor for one page visit and tears it down on navigation.
 *
 * The teardown matters more than it looks. `runAsyncView` can re-render (an
 * error retry), and the router disposes the page on every hash change, so
 * without this a Monaco instance would be left attached to a detached textarea
 * on every navigation away from an edit page.
 */
function editorMount(noteId?: string): {
  onMount: (handle: NoteFormHandle) => void;
  dispose: () => void;
} {
  let editor: RichEditorHandle | null = null;
  return {
    onMount: (handle: NoteFormHandle): void => {
      editor?.dispose();
      editor = attachRichEditor({
        textarea: handle.body,
        // Cmd-S saves through the same path the button uses, rather than
        // becoming a second way to write a note.
        onSave: handle.requestSubmit,
        ...(noteId === undefined ? {} : { noteId }),
      });
    },
    dispose: (): void => {
      editor?.dispose();
      editor = null;
    },
  };
}

export function createNotePage(context: PageContext): PageView {
  const container = el("div", { className: "panel panel--flush" });
  const rich = wantsRichEditor(context.query);
  // No note id yet — the editor falls back to its `draft` model URI.
  const mount = editorMount();

  const view = runAsyncView<readonly string[]>({
    container,
    loadingLabel: "Loading folders…",
    load: (signal) => fetchFolders(signal),
    render: (folders) =>
      noteForm({
        initial: { title: "", body: "", tags: [], folder: DEFAULT_FOLDER },
        folders: folders.length === 0 ? [DEFAULT_FOLDER] : folders,
        submitLabel: "Create note",
        cancelHref: homePath(),
        // Leaving the title blank is allowed on create: the note gets a
        // placeholder now and a real title later, when the sync daemon's
        // titling pass runs a local model over the body.
        titleFallback: placeholderTitle,
        onSubmit: async (input: NoteInput): Promise<void> => {
          const created = await createNote(input);
          navigate(created.noteId === "" ? homePath() : notePath(created.noteId));
        },
        ...(rich ? { onMount: mount.onMount } : {}),
      }),
  });

  const element = el("section", {
    className: "page page--edit",
    children: [pageHeader({ title: "New note", subtitle: "Markdown body, tags, and where the file lands." }), container],
  });

  return {
    element,
    dispose: (): void => {
      mount.dispose();
      view.dispose();
    },
  };
}

export function editNotePage(context: PageContext): PageView {
  const noteId = context.params["id"] ?? "";
  const container = el("div", { className: "panel panel--flush" });
  const rich = wantsRichEditor(context.query);
  const mount = editorMount(noteId === "" ? undefined : noteId);

  // The folder list is loaded with the note rather than lazily on focus so
  // the picker is ready the moment the form is, and so a failure to fetch it
  // surfaces through the same retry path as a failure to fetch the note.
  const view = runAsyncView<readonly [Note, readonly string[]]>({
    container,
    loadingLabel: "Loading note…",
    load: (signal) => Promise.all([fetchNote(noteId, signal), fetchFolders(signal)]),
    render: ([note, folders]) =>
      noteForm({
        initial: { title: note.title, body: note.body, tags: note.tags, folder: note.folder },
        folders: folders.length === 0 ? [note.folder] : folders,
        folderHint: "Changing it moves the note; the sync daemon moves the file on disk.",
        submitLabel: "Save changes",
        cancelHref: notePath(note.noteId),
        onSubmit: async (input: NoteInput): Promise<void> => {
          await updateNote(note.noteId, input);
          navigate(notePath(note.noteId));
        },
        onDelete: async (): Promise<void> => {
          await deleteNote(note.noteId);
          navigate(homePath());
        },
        ...(rich ? { onMount: mount.onMount } : {}),
      }),
  });

  const element = el("section", {
    className: "page page--edit",
    children: [
      el("a", { className: "back-link", attrs: { href: notePath(noteId) }, text: "← Back to note" }),
      pageHeader({ title: "Edit note" }),
      container,
    ],
  });

  return {
    element,
    dispose: (): void => {
      mount.dispose();
      view.dispose();
    },
  };
}
